import type { ReactElement, ReactNode, RefObject } from "react";
import type { DueItem, TicketKalender } from "@kundeninfo/api-client";
import { useSofortPopup } from "@kundeninfo/ui";
import { Chip } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { freieTage, popupFuerTag } from "../tickets/ticketFormat.js";
import { addDays, weekday } from "../tickets/timelineModel.js";
import { canCreateTicket, dueMark, sameDayTicket } from "../termine/dueItems.js";
import type { DueSource } from "../termine/dueItems.js";
import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";
import { DueSymbol } from "../termine/DueSourceChips.js";
import {
  ENTRY_STATES, dayHead, daySpeak, monthCellMax, monthGrid, periodText, worstState, yearDayPopup, yearMonths,
} from "./calendarModel.js";
import type { CalendarEntry, CalendarView } from "./calendarModel.js";
import { CALENDAR_TEXT } from "./calendarTexts.js";
import type { CalendarKeys } from "./useCalendarKeys.js";
import "./Kalender.css";

/**
 * DIE VIER SICHTEN DES KALENDERS (Spec D23; Übergang T-16 bis me-design `me-kalender` liefert, Wunsch W12) — CSS-Raster
 * aus `Kalender.css`, keine Diagrammbibliothek. Die Sichten zeichnen nur: Fenster, Einträge je Tag, Tastatur und Sprünge
 * kommen vom Bereich.
 *
 * - Tag: Liste der Einträge (je Zeile Symbol, Zustand, Uhrzeit, Nummer, Titel · Partner, Betrag rechtsbündig, Ticket);
 *   ist der Tag heute, zuerst der Block „Überfällig“.
 * - Woche: sieben Spalten Mo–So mit klebendem Kopf, Einträge zweizeilig; am Telefon untereinander (CSS).
 * - Monat: 7 × 4–6 mit leiser KW-Spalte, die Zeilen teilen sich die Höhe des Rollfelds (kein Sprung zwischen 4, 5 und 6
 *   Zeilen); höchstens drei Einträge je Zelle, bei sechs Zeilen zwei (G2, `monthCellMax`), dann „mehr“ (Hausregel 547,
 *   → Sicht Tag); am Telefon je Tag ein Zähler (Fläche in der Farbe des schlechtesten Zustands) und die Tagesliste des
 *   gewählten Tags darunter.
 * - Jahr: zwölf Monatskacheln, je Tag ein Feld in der Farbe des schlechtesten Zustands, Zahl und Liste im Sofort-Popup;
 *   Tag → Sicht Tag, Monatsname → Sicht Monat.
 *
 * Ein Eintrag ist ein Knopf mit 1-px-Rahmen (anklickbar) und 4-px-Streifen in der Zustandsfarbe (Kennzeichen = Fläche);
 * das Kürzel des Unternehmens steht nur in „Alle“ als Chip in der Hausfarbe. Wochenenden und Feiertage (Kalender der
 * Antwort) sind getönt, heute trägt eine Fläche am Tageskopf. Jede Zelle nennt sich mit der Vorlesestimme des Tages, jeder
 * Eintrag mit `DUE_ITEM_TEXT.speak`.
 *
 * Zustand nicht nur über die Farbe (G1, Hausregel 546): Woche, Monat, Jahr und der Zähler am Telefon tragen zur Farbe ein
 * Zeichen (`CALENDAR_TEXT.stateMarks`: ! überfällig, ● heute fällig, ○ offen, ✓ erledigt; stumm, die Vorlesestimme nennt
 * den Zustand); `CalendarLegend` erklärt die Zeichen (Sofort-Popup je Zeichen). Die Sicht Tag hat ihr beschriftetes
 * Kennzeichen. Im Jahr trägt die Fläche allein den Zustand, der Feiertag ist dort ein gestrichelter Rahmen.
 */

export type CalendarViewsProps = {
  view: CalendarView; anchor: string; heute: string; from: string;
  /** Die sichtbaren Einträge je Tag im Fenster, Schlechtestes oben. */
  byDay: ReadonlyMap<string, readonly CalendarEntry[]>;
  /** Sicht Tag, heute: die Überfälligen vor dem Tag (Block „Überfällig“). */
  overdue: readonly CalendarEntry[];
  kalender: readonly TicketKalender[];
  /** „Alle“ Unternehmen: Kürzel-Chip am Eintrag. */
  showCompany: boolean;
  phone: boolean;
  /** Ist die Antwort für dieses Fenster da? Erst dann steht „Keine Termine.“ (sonst gälte sie für das alte Fenster). */
  loaded: boolean;
  keys: CalendarKeys;
  gridRef: RefObject<HTMLDivElement | null>;
  onOpen: (e: CalendarEntry) => void;
  /** Die Ticketnummer einer Zeile (D26) öffnet das Ticket; `fromKey` ist der Eintrag der Zeile (Fokus zurück). */
  onOpenTicket: (ticketId: string, fromKey: string) => void;
  /** „Ticket anlegen“ an einem Fremdeintrag ohne Ticket (Aufgabe 12; nur Tag und Woche); `fromKey` ist der Eintrag (Fokus zurück). */
  onCreateTicket?: (item: DueItem, fromKey: string) => void;
  /** Sicht Tag dieses Datums („mehr“, Tag im Jahr). */
  onDay: (day: string) => void;
  /** Sicht Monat ab diesem Tag (Monatsname im Jahr). */
  onMonth: (day: string) => void;
};

const T = CALENDAR_TEXT;
const NONE: readonly CalendarEntry[] = [];
const STATE_ART = { OVERDUE: "fehler", DUE_TODAY: "warnung", OPEN: "ok", DONE: "aus" } as const;

/** G1: das Zeichen eines Zustands neben seiner Farbe — stumm, die Vorlesestimme von Eintrag und Tag nennt ihn. */
function StateMark({ state }: { state: CalendarEntry["state"] }): ReactElement {
  return <span className="kal__mark" data-state={state} aria-hidden="true">{T.stateMarks[state]}</span>;
}

/** G1: eine Probe der Legende — das Zeichen auf der Zustandsfläche, sein Name im Sofort-Popup und für Hilfstechnik. */
function LegendSample({ state }: { state: CalendarEntry["state"] }): ReactElement {
  const tip = useSofortPopup<HTMLSpanElement>(T.stateNames[state]);
  return (
    <span ref={tip} className="kal__legend-sample" data-state={state} role="img" aria-label={T.stateNames[state]}>
      {T.stateMarks[state]}
    </span>
  );
}

/** G1: die Legende der Zustandszeichen, Schlechtestes zuerst (der Bereich zeigt sie nicht am Telefon). */
export function CalendarLegend(): ReactElement {
  return (
    <div className="kal__legend" role="group" aria-label={T.legendName}>
      {ENTRY_STATES.map((s) => <LegendSample key={s} state={s} />)}
    </div>
  );
}

/** Klassen eines Tages: getönt am Wochenende und an Feiertagen, Fläche heute, leise außerhalb des Monats. */
function dayClass(base: string, day: string, p: Pick<CalendarViewsProps, "heute">, holidays: ReadonlyMap<string, unknown>,
                  outside = false): string {
  return [base, weekday(day) >= 5 ? `${base}--weekend` : "", holidays.has(day) ? `${base}--holiday` : "",
    day === p.heute ? `${base}--today` : "", outside ? `${base}--outside` : ""].filter((c) => c !== "").join(" ");
}

export function CalendarViews(p: CalendarViewsProps): ReactElement {
  switch (p.view) {
    case "DAY": return <DayView {...p} />;
    case "WEEK": return <WeekView {...p} />;
    case "MONTH": return <MonthView {...p} />;
    case "YEAR": return <YearView {...p} />;
  }
}

// ── Eintrag ────────────────────────────────────────────────────────────────────────────────────────────────────

type EntryProps = { entry: CalendarEntry; variant: "row" | "two" | "line"; p: CalendarViewsProps; tabbable: boolean };

/** Ein Eintrag als Knopf: `row` (Tag), `two` (Woche, zweizeilig), `line` (Monat, einzeilig). */
function Entry({ entry: e, variant, p, tabbable }: EntryProps): ReactElement {
  const tip = useSofortPopup<HTMLButtonElement>(DUE_ITEM_TEXT.popupLines(e.item, p.heute).join("\n"));
  const symbol = <DueSymbol source={e.source as DueSource} />;
  const time = e.time === null ? null : <span className="kal__time">{e.time}</span>;
  const amount = e.amount === null ? null : <span className="kal__amount">{e.amount}</span>;
  const company = p.showCompany
    ? <Chip className="kal__company" {...hausfarbeChip(p.kalender, e.item.companyShortCode)}>{e.item.companyShortCode}</Chip> : null;
  const title = <><span className="kal__title">{e.title}</span>
    {e.subtitle !== null && <span className="kal__subtitle">{e.subtitle}</span>}</>;
  const number = e.reference === null ? null : <span className="kal__number">{e.reference}</span>;
  let content: ReactNode;
  const mark = <StateMark state={e.state} />;
  if (variant === "line") {
    content = <span className="kal__line">{mark}{symbol}{time}<span className="kal__title">{e.title}</span>{company}</span>;
  } else if (variant === "two") {
    content = <>
      <span className="kal__line">{mark}{symbol}{number}{time}{company}{amount}</span><span className="kal__line">{title}</span>
    </>;
  } else {
    const due = dueMark(e.item, p.heute);
    content = <span className="kal__line">{symbol}
      <Chip className="kal__state" {...(due.art === null ? {} : { statusArt: due.art })}>{due.text}</Chip>
      {time}{number}<span className="kal__title-block">{title}</span>{company}{amount}</span>;
  }
  return (
    <button type="button" ref={tip} className={`kal__entry kal__entry--${variant}`} data-entry="" data-key={e.key}
      data-state={e.state} {...(e.target.kind === "TICKET" ? { "data-ticket": e.target.ticketId } : {})}
      tabIndex={tabbable ? 0 : -1} aria-label={DUE_ITEM_TEXT.speak(e.item, p.heute)} onClick={() => p.onOpen(e)}>
      {content}
    </button>
  );
}

/** „Ticket anlegen“ neben einem Eintrag — nur an Fremdeinträgen ohne Ticket und nur, wenn der Bereich es anbietet. */
function CreateTicket({ entry: e, p }: { entry: CalendarEntry; p: CalendarViewsProps }): ReactElement | null {
  const create = p.onCreateTicket;
  if (create === undefined || !canCreateTicket(e.item)) return null;
  return <KiKnopf kompakt className="kal__create" onClick={() => create(e.item, e.key)}>{DUE_ITEM_TEXT.createTicket}</KiKnopf>;
}

/** Liste der Einträge als Zeilen (Tag, Tagesliste am Telefon): Eintrag und — nach D26 — das Fälligkeitsticket daneben. */
function EntryList({ entries, p, withCreate = false }: { entries: readonly CalendarEntry[]; p: CalendarViewsProps;
                    withCreate?: boolean }): ReactElement {
  if (entries.length === 0) return <p className="kal__empty">{p.loaded ? T.empty : ""}</p>;
  return (
    <ul className="kal__list">
      {entries.map((e) => (
        <li key={e.key} className="kal__list-row">
          <Entry entry={e} variant="row" p={p} tabbable />
          {withCreate && <CreateTicket entry={e} p={p} />}
          {e.ticket !== null && <TicketLink entry={e} ticket={e.ticket} p={p} />}
        </li>
      ))}
    </ul>
  );
}

/** D26: die Ticketnummer neben dem Eintrag — am selben Tag nur die Nummer, sonst mit dem Tag des Tickets. */
function TicketLink({ entry: e, ticket: t, p }: { entry: CalendarEntry; ticket: NonNullable<CalendarEntry["ticket"]>;
                                                  p: CalendarViewsProps }): ReactElement {
  return (
    <KiKnopf art="leise" kompakt className="kal__ticket" onClick={() => p.onOpenTicket(t.id, e.key)}>
      {sameDayTicket(e.item) ? t.displayNumber : DUE_ITEM_TEXT.ticketOther(t.displayNumber, t.dueDate)}
    </KiKnopf>
  );
}

/** Ein Block mit klebendem Kopf; `jumpTarget`: der Kopf nimmt nach einem Sprung in die Sicht den Fokus (G1). */
function Block({ head, children, className = "", jumpTarget = false }: { head: string; children: ReactNode; className?: string;
                                                                         jumpTarget?: boolean }): ReactElement {
  return (
    <section className={`kal__block ${className}`.trim()} aria-label={head}>
      <h2 className="kal__block-head" {...(jumpTarget ? { tabIndex: -1, "data-jump-target": "" } : {})}>{head}</h2>
      {children}
    </section>
  );
}

// ── Tag ────────────────────────────────────────────────────────────────────────────────────────────────────────

function DayView(p: CalendarViewsProps): ReactElement {
  return (
    <div className="kal__day">
      {p.overdue.length > 0 && (
        <Block head={T.overdueBlock} className="kal__block--overdue"><EntryList entries={p.overdue} p={p} withCreate /></Block>
      )}
      <Block head={periodText("DAY", p.anchor)} jumpTarget><EntryList entries={p.byDay.get(p.anchor) ?? NONE} p={p} withCreate /></Block>
    </div>
  );
}

// ── Raster (Woche, Monat) ──────────────────────────────────────────────────────────────────────────────────────

/** Eine Tageszelle des Rasters: Tabstopp, Vorlesestimme, Tönung; Klick setzt den Tabstopp und öffnet die Einträge des
 *  Tages (Woche: „Ticket anlegen“, M1; Telefon: Tagesliste). */
function Cell(p: { day: string; v: CalendarViewsProps; holidays: ReadonlyMap<string, unknown>; outside?: boolean;
                   head: ReactNode; children: ReactNode }): ReactElement {
  const entries = p.v.byDay.get(p.day) ?? NONE;
  return (
    <div role="gridcell" data-day={p.day} className={dayClass("kal__cell", p.day, p.v, p.holidays, p.outside)}
      tabIndex={p.day === p.v.keys.focusDay ? 0 : -1} aria-label={daySpeak(p.day, entries)}
      {...(p.day === p.v.heute ? { "aria-current": "date" as const } : {})} onClick={() => p.v.keys.choose(p.day)}>
      {p.head}
      {p.children}
    </div>
  );
}

/** Kopf einer Zelle mit dem Feiertag bzw. Wochenende im Sofort-Popup (wie Board und Zeitleiste, 132). */
function CellHead(p: { className: string; text: string; day: string; kalender: readonly TicketKalender[] }): ReactElement {
  const tip = useSofortPopup<HTMLSpanElement>(popupFuerTag(p.day, p.kalender));
  return <span className={p.className} ref={tip}>{p.text}</span>;
}

function gridProps(p: CalendarViewsProps, className: string) {
  return { className, role: "grid", "aria-label": periodText(p.view, p.anchor), ref: p.gridRef,
    onKeyDown: p.keys.onKeyDown, onBlur: p.keys.onBlur } as const;
}

function WeekView(p: CalendarViewsProps): ReactElement {
  const holidays = freieTage(p.kalender);
  const days = Array.from({ length: 7 }, (_, i) => addDays(p.from, i));
  return (
    <div {...gridProps(p, "kal__week")}>
      <div role="row" className="kal__week-row">
        {days.map((day) => (
          <Cell key={day} day={day} v={p} holidays={holidays}
            head={<CellHead className="kal__day-head" text={dayHead(day)} day={day} kalender={p.kalender} />}>
            <div className="kal__entries">
              {(p.byDay.get(day) ?? NONE).map((e) => (
                <div key={e.key} className="kal__entryblock">
                  <Entry entry={e} variant="two" p={p} tabbable={p.keys.insideDay === day} />
                  {/* Nur in der gewählten Tagesspalte (Enter oder Klick, M1): ein Tabstopp je Tag statt je Eintrag. */}
                  {p.keys.insideDay === day && <CreateTicket entry={e} p={p} />}
                </div>
              ))}
            </div>
          </Cell>
        ))}
      </div>
    </div>
  );
}

function MonthView(p: CalendarViewsProps): ReactElement {
  const holidays = freieTage(p.kalender);
  const month = p.anchor.slice(0, 7);
  const selected = p.byDay.get(p.keys.focusDay) ?? NONE;
  const weeks = monthGrid(p.anchor);
  const cellMax = monthCellMax(weeks.length);
  return (
    <>
      <div {...gridProps(p, "kal__month")} data-rows={weeks.length}>
        <div role="row" className="kal__month-head">
          <div role="columnheader" className="kal__week-number">{T.calendarWeek}</div>
          {T.weekdaysShort.map((w) => <div key={w} role="columnheader">{w}</div>)}
        </div>
        {weeks.map((week) => (
          <div key={week.days[0]} role="row" className="kal__month-row">
            <div role="rowheader" className="kal__week-number">{week.kw}</div>
            {week.days.map((day) => {
              const entries = p.byDay.get(day) ?? NONE;
              const worst = worstState(entries);
              const inside = p.keys.insideDay === day;
              return (
                <Cell key={day} day={day} v={p} holidays={holidays} outside={!day.startsWith(month)}
                  head={<CellHead className="kal__day-number" text={String(Number(day.slice(8, 10)))} day={day} kalender={p.kalender} />}>
                  {p.phone
                    ? worst !== null && (
                      <Chip className="kal__count" statusArt={STATE_ART[worst]}>
                        <StateMark state={worst} /><span className="kal__count-number">{entries.length}</span>
                      </Chip>
                    )
                    : <>
                      {entries.slice(0, cellMax).map((e) => (
                        <Entry key={e.key} entry={e} variant="line" p={p} tabbable={inside} />
                      ))}
                      {entries.length > cellMax && (
                        // Hausregel 547 (Entscheid 103): Ein Überlauf-Knopf heißt „mehr“ — kursiv, flächig wie ein Reiter;
                        // die Anzahl steht aufrecht daneben.
                        <button type="button" className="kal__more" data-entry="" tabIndex={inside ? 0 : -1}
                          onClick={(ev) => { ev.stopPropagation(); p.onDay(day); }}>
                          {T.more}{" "}<span className="kal__more-count">{entries.length - cellMax}</span>
                        </button>
                      )}
                    </>}
                </Cell>
              );
            })}
          </div>
        ))}
      </div>
      {p.phone && (
        <div className="kal__day-list">
          <Block head={periodText("DAY", p.keys.focusDay)}><EntryList entries={selected} p={p} /></Block>
        </div>
      )}
    </>
  );
}

// ── Jahr ───────────────────────────────────────────────────────────────────────────────────────────────────────

/** Ein Tag im Jahr: Feld in der Farbe des schlechtesten Zustands mit seinem Zeichen (G1), Zahl und Liste (und Feiertag)
 *  im Sofort-Popup. */
function YearDay(p: { day: string; v: CalendarViewsProps; holidays: ReadonlyMap<string, unknown> }): ReactElement {
  const entries = p.v.byDay.get(p.day) ?? NONE;
  const worst = worstState(entries);
  const tip = useSofortPopup<HTMLDivElement>([entries.length > 0 ? yearDayPopup(p.day, entries) : "",
    popupFuerTag(p.day, p.v.kalender)].filter((t) => t !== "").join("\n"));
  return (
    <div role="gridcell" ref={tip} data-day={p.day} {...(worst === null ? {} : { "data-state": worst })}
      className={dayClass("kal__year-day", p.day, p.v, p.holidays)} tabIndex={p.day === p.v.keys.focusDay ? 0 : -1}
      aria-label={daySpeak(p.day, entries)} {...(p.day === p.v.heute ? { "aria-current": "date" as const } : {})}
      onClick={() => p.v.onDay(p.day)}>
      {Number(p.day.slice(8, 10))}
      {worst !== null && <StateMark state={worst} />}
    </div>
  );
}

function YearView(p: CalendarViewsProps): ReactElement {
  const holidays = freieTage(p.kalender);
  const year = Number(p.anchor.slice(0, 4));
  return (
    <div className="kal__year" ref={p.gridRef} onKeyDown={p.keys.onKeyDown} onBlur={p.keys.onBlur}>
      {yearMonths(year).map(({ month }, i) => (
        <section key={month} className="kal__month-tile" aria-label={T.period.month(T.months[i]!, year)}>
          <KiKnopf art="leise" kompakt className="kal__month-name" onClick={() => p.onMonth(`${month}-01`)}>{T.months[i]}</KiKnopf>
          <div role="grid" className="kal__year-grid" aria-label={T.period.month(T.months[i]!, year)}>
            {monthGrid(`${month}-01`).map((week) => (
              <div key={week.days[0]} role="row" className="kal__year-row">
                {week.days.map((day) => (day.startsWith(month)
                  ? <YearDay key={day} day={day} v={p} holidays={holidays} />
                  : <span key={day} className="kal__gap" aria-hidden="true" />))}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
